import { ARTICLES } from './articlesData';

const AppBanner = ({ onGetStarted, description, buttonText }) => (
  <div style={styles.banner} className="titler-blog-banner">
    <div style={styles.bannerInner}>
      <div style={styles.bannerText}>
        <span style={styles.bannerLogo}>Titler</span>
        <span style={styles.bannerDesc}>{description || 'AI-powered keyword analysis and title optimization for creators'}</span>
      </div>
      <button onClick={onGetStarted} style={styles.bannerBtn}>{buttonText || 'Try it free'}</button>
    </div>
  </div>
);

const BlogPage = ({ onReadArticle, onGetStarted }) => {
  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <AppBanner onGetStarted={onGetStarted} />

        <div style={styles.header}>
          <h1 style={styles.title}>Blog</h1>
          <p style={styles.subtitle}>Tips, insights, and strategies for content creators</p>
        </div>

        <div style={styles.grid}>
          {[...ARTICLES].reverse().map((article) => (
            <article
              key={article.slug}
              style={styles.card}
              className="titler-blog-card"
              onClick={() => onReadArticle(article.slug)}
            >
              <div style={styles.cardTags}>
                {article.tags.map(tag => (
                  <span key={tag} style={styles.tag}>{tag}</span>
                ))}
              </div>
              <h2 style={styles.cardTitle}>{article.title}</h2>
              <p style={styles.cardExcerpt}>{article.excerpt}</p>
              <div style={styles.cardMeta}>
                <span>{article.author}</span>
                <span style={styles.metaDot}>&middot;</span>
                <span>{article.date}</span>
                <span style={styles.metaDot}>&middot;</span>
                <span>{article.readTime}</span>
              </div>
            </article>
          ))}
        </div>
      </div>

      <style>{CSS}</style>
    </div>
  );
};

const ArticlePage = ({ slug, onBack, onGetStarted }) => {
  const article = ARTICLES.find(a => a.slug === slug);
  if (!article) {
    return (
      <div style={styles.page}>
        <div style={{ ...styles.container, textAlign: 'center', paddingTop: '80px' }}>
          <h2 style={{ color: '#e2e8f0', marginBottom: '16px' }}>Article not found</h2>
          <button onClick={onBack} style={styles.backBtn}>Back to Blog</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.articleContainer}>
        <AppBanner onGetStarted={onGetStarted} />

        <button onClick={onBack} style={styles.backBtn}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to Blog
        </button>

        <div style={styles.articleTags}>
          {article.tags.map(tag => (
            <span key={tag} style={styles.tag}>{tag}</span>
          ))}
        </div>

        <h1 style={styles.articleTitle}>{article.title}</h1>

        <div style={styles.articleMeta}>
          <span>{article.author}</span>
          <span style={styles.metaDot}>&middot;</span>
          <span>{article.date}</span>
          <span style={styles.metaDot}>&middot;</span>
          <span>{article.readTime}</span>
        </div>

        <div style={styles.articleBody}>
          <MarkdownRenderer
            content={article.content}
            onGetStarted={onGetStarted}
          />
        </div>

        <AppBanner
          onGetStarted={onGetStarted}
          description="Stop guessing at titles. Analyze any video's keywords and let AI generate click-worthy alternatives."
          buttonText="Start analyzing"
        />
      </div>

      <style>{CSS}</style>
    </div>
  );
};

// Simple markdown renderer for articles (handles ##, **, -, |tables|, backticks)
const MarkdownRenderer = ({ content, onGetStarted }) => {
  const lines = content.trim().split('\n');
  const elements = [];
  let i = 0;
  const totalLines = lines.length;
  const bannerInsertPoint = Math.floor(totalLines / 3);
  let bannerInserted = false;

  while (i < lines.length) {
    const line = lines[i];

    // Insert mid-article banner at ~1/3 of the way through
    if (!bannerInserted && i >= bannerInsertPoint && onGetStarted) {
      elements.push(
        <AppBanner
          key="mid-banner"
          onGetStarted={onGetStarted}
          description="Discover what keywords top creators use — paste any YouTube URL and get instant analysis."
          buttonText="Analyze a video"
        />
      );
      bannerInserted = true;
    }

    // Heading
    if (line.startsWith('## ')) {
      elements.push(<h2 key={i} style={md.h2}>{line.slice(3)}</h2>);
      i++;
      continue;
    }

    // Table
    if (line.includes('|') && line.trim().startsWith('|')) {
      const tableLines = [];
      while (i < lines.length && lines[i].includes('|') && lines[i].trim().startsWith('|')) {
        tableLines.push(lines[i]);
        i++;
      }
      const rows = tableLines
        .filter(l => !l.match(/^\|\s*[-|]+\s*\|$/))
        .map(l => l.split('|').filter(c => c.trim()).map(c => c.trim()));
      if (rows.length > 0) {
        elements.push(
          <table key={i} style={md.table}>
            <thead>
              <tr>{rows[0].map((c, ci) => <th key={ci} style={md.th}>{c}</th>)}</tr>
            </thead>
            <tbody>
              {rows.slice(1).map((row, ri) => (
                <tr key={ri}>{row.map((c, ci) => <td key={ci} style={md.td}>{c}</td>)}</tr>
              ))}
            </tbody>
          </table>
        );
      }
      continue;
    }

    // List item
    if (line.startsWith('- ')) {
      const items = [];
      while (i < lines.length && lines[i].startsWith('- ')) {
        items.push(lines[i].slice(2));
        i++;
      }
      elements.push(
        <ul key={i} style={md.ul}>
          {items.map((item, idx) => <li key={idx} style={md.li}><InlineText text={item} /></li>)}
        </ul>
      );
      continue;
    }

    // Numbered list
    if (/^\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s/, ''));
        i++;
      }
      elements.push(
        <ol key={i} style={md.ol}>
          {items.map((item, idx) => <li key={idx} style={md.li}><InlineText text={item} /></li>)}
        </ol>
      );
      continue;
    }

    // Empty line
    if (!line.trim()) {
      i++;
      continue;
    }

    // Paragraph
    elements.push(<p key={i} style={md.p}><InlineText text={line} /></p>);
    i++;
  }

  return <>{elements}</>;
};

// Handles **bold** and `code` inline
const InlineText = ({ text }) => {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i} style={{ color: '#e2e8f0', fontWeight: '600' }}>{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith('`') && part.endsWith('`')) {
          return <code key={i} style={md.code}>{part.slice(1, -1)}</code>;
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
};

const CSS = `
.titler-blog-card {
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}
.titler-blog-card:hover {
  transform: translateY(-4px) !important;
  border-color: rgba(124,58,237,0.3) !important;
  box-shadow: 0 8px 32px rgba(124,58,237,0.1) !important;
}
.titler-blog-banner-btn:hover {
  transform: scale(1.03) !important;
  box-shadow: 0 4px 20px rgba(124,58,237,0.4) !important;
}
`;

const styles = {
  page: {
    minHeight: '100vh',
    background: '#000',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: '#e2e8f0',
  },
  banner: {
    marginBottom: '40px',
  },
  bannerInner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 24px',
    borderRadius: '14px',
    background: 'linear-gradient(135deg, rgba(124,58,237,0.12) 0%, rgba(99,102,241,0.08) 100%)',
    border: '1px solid rgba(124,58,237,0.2)',
  },
  bannerText: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  bannerLogo: {
    fontSize: '18px',
    fontWeight: '800',
    letterSpacing: '-0.5px',
    background: 'linear-gradient(135deg, #a78bfa, #818cf8, #6366f1)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  bannerDesc: {
    fontSize: '13px',
    color: '#94a3b8',
  },
  bannerBtn: {
    padding: '8px 20px',
    borderRadius: '10px',
    border: 'none',
    background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
    color: '#fff',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    flexShrink: 0,
  },
  container: {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '48px 24px',
  },
  header: {
    marginBottom: '48px',
  },
  title: {
    fontSize: '36px',
    fontWeight: '800',
    margin: '0 0 8px',
    letterSpacing: '-1.5px',
    background: 'linear-gradient(135deg, #e2e8f0, #a78bfa)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  subtitle: {
    fontSize: '16px',
    color: '#64748b',
    margin: 0,
  },
  grid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  card: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '16px',
    padding: '28px',
    cursor: 'pointer',
  },
  cardTags: {
    display: 'flex',
    gap: '8px',
    marginBottom: '14px',
    flexWrap: 'wrap',
  },
  tag: {
    padding: '3px 10px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: '600',
    background: 'rgba(124,58,237,0.15)',
    color: '#a78bfa',
    letterSpacing: '0.3px',
  },
  cardTitle: {
    fontSize: '22px',
    fontWeight: '700',
    margin: '0 0 10px',
    color: '#e2e8f0',
    letterSpacing: '-0.5px',
    lineHeight: '1.3',
  },
  cardExcerpt: {
    fontSize: '14px',
    color: '#94a3b8',
    lineHeight: '1.7',
    margin: '0 0 16px',
  },
  cardMeta: {
    fontSize: '12px',
    color: '#475569',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  metaDot: {
    color: '#334155',
  },
  // Article page
  articleContainer: {
    maxWidth: '680px',
    margin: '0 auto',
    padding: '48px 24px 80px',
  },
  backBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '8px 16px',
    borderRadius: '8px',
    border: '1px solid rgba(255,255,255,0.1)',
    background: 'transparent',
    color: '#94a3b8',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    fontFamily: 'inherit',
    marginBottom: '32px',
  },
  articleTags: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  articleTitle: {
    fontSize: '36px',
    fontWeight: '800',
    margin: '0 0 16px',
    letterSpacing: '-1.5px',
    lineHeight: '1.15',
    color: '#e2e8f0',
  },
  articleMeta: {
    fontSize: '13px',
    color: '#475569',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '40px',
    paddingBottom: '32px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  articleBody: {
    lineHeight: '1.8',
  },
};

const md = {
  h2: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#e2e8f0',
    margin: '40px 0 16px',
    letterSpacing: '-0.5px',
  },
  p: {
    fontSize: '15px',
    color: '#94a3b8',
    margin: '0 0 16px',
    lineHeight: '1.8',
  },
  ul: {
    margin: '0 0 16px',
    paddingLeft: '24px',
  },
  ol: {
    margin: '0 0 16px',
    paddingLeft: '24px',
  },
  li: {
    fontSize: '15px',
    color: '#94a3b8',
    marginBottom: '8px',
    lineHeight: '1.7',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    margin: '16px 0 24px',
    fontSize: '14px',
  },
  th: {
    textAlign: 'left',
    padding: '10px 14px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    color: '#cbd5e1',
    fontWeight: '600',
    fontSize: '13px',
  },
  td: {
    padding: '10px 14px',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    color: '#94a3b8',
  },
  code: {
    background: 'rgba(124,58,237,0.12)',
    color: '#c4b5fd',
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '13px',
    fontFamily: 'monospace',
  },
};

export { BlogPage, ArticlePage };
